import React, { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { trackEvent } from '@gjensidige/core-analytics/lib/track-event';
import { TrackingContext } from '@gjensidige/core-analytics/lib/tracking-context';
import { TrackEventProps } from '@gjensidige/core-analytics/lib/types';
import { Close } from '@gjensidige/core-icons/lib/close';
import { Error } from '@gjensidige/core-icons/lib/error';
import { Info } from '@gjensidige/core-icons/lib/info';
import { ThumbUp } from '@gjensidige/core-icons/lib/thumb-up';
import { Warning } from '@gjensidige/core-icons/lib/warning';

import ClickCollector from '../click-collector';
import { AlertTypeProps } from '../types';
import { DEFAULT_TRACKING } from './tracking';

import styles from '../../less/default/index.module.less';

interface DefaultProps extends AlertTypeProps {
  /**
   * Toggles whether icon is hidden
   * @default true
   */
  showIcon?: boolean;
}

interface OptionalProps {
  /**
   * Children (can be used to represent title, text and action if you need to control the styling on a higher level)
   */
  children?: React.ReactNode;
  /**
   * Title content for alert
   */
  title?: string;
  /**
   * Close button for alert
   */
  closable?: boolean;
  /**
   * Handle side effects of closing the alert
   */
  onCloseClick?: () => void;
  /**
   * Text content for alert
   */
  text?: string;
  /**
   * Action for alert
   */
  action?: React.ReactNode;
  className?: string;
}

export type ComponentProps = AlertTypeProps &
  DefaultProps &
  OptionalProps &
  TrackEventProps &
  React.HTMLAttributes<HTMLElement>;

const AlertIcons = {
  error: <Error color="#E1403C" className={styles.icon} />,
  information: <Info color="#006DC1" className={styles.icon} />,
  success: <ThumbUp color="#0B7745" className={styles.icon} />,
  warning: <Warning color="#DE7F32" className={styles.icon} />
};

export const Default: React.FunctionComponent<ComponentProps> = ({
  // Default props
  alertType = 'information',
  showIcon = true,
  enableTracking = true,
  tracking = DEFAULT_TRACKING,

  children,
  // Optional props
  title,
  text,
  className,
  closable = false,
  onCloseClick,

  // Old props?
  action,

  ...rest
}) => {
  const { options: trackingOptions } = useContext(TrackingContext);
  const [showAlert, setShowAlert] = useState(true);

  const handleCloseClick = () => {
    onCloseClick?.();
    setShowAlert(false);
  };

  const track = (overrides) => {
    if (enableTracking) {
      trackEvent(
        {
          ...DEFAULT_TRACKING,
          ...tracking,
          ...overrides
        },
        trackingOptions
      );
    }
  };

  useEffect(() => {
    if (enableTracking && !tracking.eventLabel && !title) {
      console.warn(
        'Missing value eventLabel for tracking in <Alert>. Please add a tracking object with the eventLabel property.'
      );
    }

    track({
      eventAction: 'impression',
      eventCategory: `alert_${alertType}`,
      eventLabel: tracking.eventLabel || title || 'default-alert-label'
    });
  }, []);

  return showAlert ? (
    <section className={classNames(styles[alertType], className)} {...rest}>
      <div
        className={styles.content}
        data-testid={`default-alert-${alertType}`}
      >
        {showIcon && AlertIcons[alertType]}
        <div>
          {title && <header className={styles.title}>{title}</header>}
          {text && <p className={styles.text}>{text}</p>}
          {action && (
            <ClickCollector track={track} addClass>
              {action}
            </ClickCollector>
          )}
          {children && (
            <ClickCollector track={track}>{children}</ClickCollector>
          )}
        </div>
      </div>
      {closable && (
        <button
          aria-label="close alert-box"
          className={styles.closeButton}
          onClick={handleCloseClick}
          data-testid="default-close-button"
        >
          <Close />
        </button>
      )}
    </section>
  ) : null;
};

export const Alert = Default;
